import { toApiError } from "./api";

export interface UploadedFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  kind: "image" | "pdf" | "docx" | "xlsx" | "pptx" | "text" | "binary";
  hasText: boolean;
}

const MAX = 8 * 1024 * 1024;

/** Big photos are downscaled in the browser (≤1600px JPEG) so they fit limits and cost fewer vision tokens. */
async function shrinkImage(file: File): Promise<File> {
  if (
    !/^image\/(png|jpeg|webp)$/.test(file.type) ||
    (file.size < 900_000 && file.type !== "image/png")
  )
    return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1_500_000) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.86));
    return blob
      ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" })
      : file;
  } catch {
    return file;
  }
}

export async function uploadFile(original: File): Promise<UploadedFile> {
  const file = await shrinkImage(original);
  if (file.size > MAX) throw new Error(`${file.name}: file too large (8 MB max)`);
  const res = await fetch("/api/files", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "x-qn-client": "web",
      "x-file-name": encodeURIComponent(file.name),
      "content-type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!res.ok) throw await toApiError(res);
  return ((await res.json()) as { file: UploadedFile }).file;
}
